import {DialogActions, DialogTitle, DialogContent, Button, Tabs, Tab, Checkbox, List, ListItemButton, ListItemIcon, ListItemText} from "@mui/material";
import {drwElevationName} from "./drwDefaultElevation";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {hasPreambleContent} from "../../../../misc/richtext/preambleContent";
import {Opening} from "../../../../api/Openings";
import {useContext, useEffect, useState} from "react";
import {useTableColumns, useTableData} from "../../../../misc/scroller/Table";
import {useProject, useProjectId} from "../ProjectName";
import {currentTab, openingsTableName} from "./Openings";
import {OpeningsPDF} from "./OpeningsPDF";
import {PDFInfo, PrintManagerContext} from "../../../../misc/pdf/PrintManager";
import {distinct, distinctByKey, first, orderByAscending, useAsyncAction} from "nate-react-api-helpers";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {ColumnTab} from "./pdf/ColumnTab";
import {PreambleTab} from "./pdf/PreambleTab";
import {TabWrapper} from "../TabWrapper";
import {
    currentQuoteTab,
    frameConstructionLookup,
    handingLookup,
    lookupHollowMetalMaterial,
    quoteOpeningsTableName,
} from "../../quote/openings/QuoteOpenings";
import {
    woodPLAM,
    hollowMetalDoor,
    hollowMetalFrame, woodDoor,
} from "../../quote/openings/Const";
import {useSubmittalPreferences} from "../../../../misc/pdf/useSubmittalPreferences";
import {Project} from "../../../../api/Projects";
import {Company} from "../../../../api/Companies";
import {api} from "../../../../api/API";
import {RenderingStatus} from "../../../../api/ElevationTool";
import {SubmittalRenderGate} from "./SubmittalRenderGate";
import {QuoteOpening} from "../../../../api/QuoteOpenings";
import {SmallGreyButton} from "../../../../misc/SmallGreyButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {anchorLookupOptions} from "../extras/CreateProduct";
import {defaultPages, PageItem, PagesTab} from "./pdf/PagesTab";
import {PhaseFilterSelect} from "./PhaseFilterSelect";
import {matchesPhaseFilter, phaseId} from "../../../../misc/Phase";
import {Contact} from "../../../../api/Contacts";
import {useCurrentContractor} from "../../quote/CurrentContractor";
import {useIsQuote} from "../../quote/QuoteContainer";
import {useAlternative} from "../../quote/alternative/Alternative";
import {loadGlazingSections} from "../glazingSubmittal/GlazingSections";
import {OpeningsTab, OpeningWithSelected} from "./pdf/OpeningsTab";
import {useDropdownDataIMP, ValueItem} from "../../settings/DropDowns";
import {PausePricingCover} from "../../quote/pricing/PausePricingCover";
import {useSnackAction} from "../hardware/SnackAction";

export type ExportColumn = {
    display: string;
    key: keyof Opening & keyof QuoteOpening;

    forFrame?: boolean;
    forDoor?: boolean;

    // name of the matching column in the openings table when it differs from display
    tableColumn?: string;
    render?: (input: Opening|QuoteOpening, drawingInfo: DrawingInfo) => string;
}

export const openingColumns: ExportColumn[] = [{
    display: "Opening",
    key: "name"
},{
    display: "Qty",
    key: "qty"
},{
    display: "Floor",
    key: "floor"
},{
    display: "Phase",
    key: "phase"
},{
    display: "Ext",
    key: "exterior",
    render: (input) => input.exterior ? "Yes" : "No"
},{
    display: "Location",
    key: "locationOne"
},{
    display: "To\nFrom",
    tableColumn: "To/From",
    key: "locationTransition"
},{
    display: "Location 2",
    key: "locationTwo"
},{
    display: "Type",
    key: "openingType",
    forFrame: true,
    forDoor: true,
},{
    display: "Hand",
    key: "handing",
    forFrame: true,
    forDoor: true,
},{
    display: "Label",
    key: "label",
    forFrame: true,
    forDoor: true,
},{
    display: "Frame\nSeries",
    key: "frameSeries",
    forFrame: true
},{
    display: "Frame\nMaterial",
    key: "frameMaterial",
    forFrame: true
},{
    display: "Frame\nGauge",
    key: "frameGauge",
    forFrame: true
},{
    display: "Screen\nElev",
    tableColumn: "Screen Elevation",
    key: "screenElevation",
    forFrame: true
},{
    display: "Nominal\nWidth",
    key: "nominalWidth",
    forFrame: true,
    forDoor: true,
    render: input => {
        if(input.openingType === "Pair") {
            return `${input.nominalWidth}, ${input.inactiveDoorWidth}`
        }

        return input.nominalWidth;
    }
},{
    display: "Nominal\nHeight",
    key: "nominalHeight",
    forFrame: true,
    forDoor: true,
},{
    display: "Frame\nProfile",
    key: "frameProfile",
    forFrame: true
},{
    display: "Anchor",
    key: "frameAnchorProduct",
    forFrame: true,
    render: (input: Opening|QuoteOpening) => {
        if(input.drwHasAnchors) return (input.drwAnchorSummary ? input.drwAnchorSummary + " " : "") + "(screen)";

        const name = input.frameAnchorProductName
        if(!name) return "";
        let parts = name.split(": ")
        return parts[0];
    }
},{
    display: "Wall\nType",
    key: "drawingWallLabel",
    forFrame: true,
    forDoor: true,
},{
    display: "Jamb\nDepth",
    key: "jambDepth",
    forFrame: true,
},{
    display: "Frame\nConstruction",
    key: "frameConstruction",
    forFrame: true
},{
    display: "Door\nSeries",
    key: "doorSeries",
    forDoor: true
},{
    display: "Door\nMaterial",
    key: "doorMaterial",
    forDoor: true,
    render: (input: Opening|QuoteOpening, drawingInfo: DrawingInfo) => {
        return drawingInfo.doorMaterialsLookup[input.doorMaterial] || input.doorMaterial
    }
},{
    display: "Door\nFinish",
    key: "doorFinish",
    forDoor: true,
    render: (input, drawingInfo) => {
        return drawingInfo.doorFinishesLookup[input.doorFinish] || input.doorFinish
    }
},{
    display: "Door\nGauge",
    key: "doorGauge",
    forDoor: true
},{
    display: "Door\nThickness",
    key: "doorThickness",
    forDoor: true
},{
    display: "Door\nElevation",
    key: "doorElevation",
    forDoor: true
},{
    display: "Door\nCore",
    key: "doorCore",
    forDoor: true
},{
    display: "Door\nSeam",
    key: "doorSeam",
    forDoor: true
},{
    display: "Hardware\nHeading",
    key: "hardwareGroupName"
},{
    display: "Remarks",
    tableColumn: "Notes",
    key: "notes"
}]

// "{elevationName}-{label}" for a drw elevation drawing (empty when the elevation is unset). Both
// production Opening and QuoteOpening carry the drw render fields.
function elevationDrawingValue(name: string | undefined, label: string | undefined): string {
    if(!name) return "";
    return label ? `${name}-${label}` : name;
}

// a drw rendering to draw: the image file and the label printed under it.
type RenderingRef = {file: number; label: string};

// distinct drw renderings (one per file) with their label, sorted by label including its suffix
// (e.g. STD-A before STD-B). `get` pulls the rendering file id + label off an opening, or undefined
// when the opening has no rendering of that kind.
function distinctRenderingsByLabel(
    data: (Opening|QuoteOpening)[],
    get: (o: Opening|QuoteOpening) => {file: number | undefined; label: string} | undefined,
): RenderingRef[] {
    const items = data
        .map(o => {
            const v = get(o);
            return v && v.file ? {file: v.file, label: v.label} : null;
        })
        .filter((v): v is RenderingRef => !!v);
    const unique = distinctByKey(items, v => String(v.file));
    unique.sort((a, b) => a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: "base"}));
    return unique;
}

// page-driven openings-table columns for the drw elevation drawings. Appended to the table
// when the matching elevation page is enabled (see getShopDrawingPDF) — not user-pickable, so
// they're not part of openingColumns / the Columns tab. Keys reuse a benign shared column key
// (render always provides the value).
export const screenDrawingColumn: ExportColumn = {
    display: "Screen\nDrawing",
    key: "screenElevation",
    // guard on the field's presence (both Opening and QuoteOpening carry it) so a row without a
    // drw label renders blank rather than "undefined".
    render: input => "drwScreenElevationLabel" in input
        ? elevationDrawingValue(drwElevationName(input, "screen"), input.drwScreenElevationLabel)
        : "",
};
export const doorDrawingColumn: ExportColumn = {
    display: "Door\nDrawing",
    key: "doorElevation",
    render: input => "drwDoorElevationLabel" in input
        ? elevationDrawingValue(drwElevationName(input, "door"), input.drwDoorElevationLabel)
        : "",
};
export const hardwareDrawingColumn: ExportColumn = {
    display: "Hardware\nDrawing",
    key: "hardwareGroupName",
    render: input => "drwHardwareElevationLabel" in input
        ? elevationDrawingValue(drwElevationName(input, "hardware"), input.drwHardwareElevationLabel)
        : "",
};

// exportColumnsForTable picks the export columns that match the given openings table column names, in table order
export function exportColumnsForTable(tableColumnNames: string[]) {
    return tableColumnNames
        .map(name => openingColumns.find(c => (c.tableColumn || c.display.replace(/\n/g, " ")) === name))
        .filter(v => !!v) as ExportColumn[];
}

export function MakeSubmittal(props: {
    onDone(): void;
}) {
    const project = useProjectId();
    const tableName = openingsTableName(project, currentTab);
    const openings = useTableData(tableName) as Opening[]

    return (
        <MakeSubmittalInner onDone={props.onDone} openings={openings} tableName={tableName} />
    )
}

export function MakeQuoteSubmittal(props: {}) {
    const [show, setShow] = useState(false);
    const project = useProjectId();
    const tableName = quoteOpeningsTableName(project, currentQuoteTab);
    const openings = useTableData(tableName) as QuoteOpening[]

    return (
        <>
            <SmallGreyButton startIcon={<AddCircleIcon />} onClick={() => setShow(true)}>
                Create Submittal
            </SmallGreyButton>
            {show && <MakeSubmittalInner onDone={() => setShow(false)} openings={openings} tableName={tableName} />}
        </>
    )
}

export function MakeSubmittalInner(props: {
    openings: (Opening|QuoteOpening)[]
    tableName: string;
    onDone(): void;
}) {
    const [selected, setSelected] = useState<ExportColumn[]>([]);
    const projectId = useProjectId();
    const printCtx = useContext(PrintManagerContext);
    const project = useProject();
    const isQuote = useIsQuote();
    const alternative = useAlternative();

    // without saved preferences, default to the openings table's visible columns in their current order
    const tableColumns = useTableColumns(props.tableName);
    const [useTableDefault, setUseTableDefault] = useState(false);

    useEffect(() => {
        if(!useTableDefault) return;

        const fromTable = exportColumnsForTable(tableColumns);
        setSelected(fromTable.length > 0 ? fromTable : openingColumns);
        setUseTableDefault(false);
    }, [useTableDefault, tableColumns]);

    const prefs = useSubmittalPreferences({
        project: projectId,
        kind: "shop-drawing",
        onBlank: () => setUseTableDefault(true),
        onLoad: (list: ShopDrawingSubmittalPrefs) => {
            if (list instanceof Array) {
                setSelected(list.map(l => first(openingColumns, c => c.key === l))
                    .filter(v => !!v) as ExportColumn[])
            } else if ("version" in list && list.version === 1) {
                setSelected(list.columns.map(l => first(openingColumns, c => c.key === l))
                    .filter(v => !!v) as ExportColumn[])
                setPreamble(list.preamble)
            } else if ("version" in list && list.version === 2) {
                setSelected(list.columns.map(l => first(openingColumns, c => c.key === l))
                    .filter(v => !!v) as ExportColumn[])
                setPreamble(list.preamble)
                setPages(list.pages as any)
                setSplitByPhase(!!list.splitByPhase)
                setPhaseFilter(list.phaseFilter || [])
            }
        }
    });

    const [preamble, setPreamble] = useState("")
    const [renderGate, setRenderGate] = useState<RenderingStatus | null>(null);
    const [tab, setTab] = useState("pages");
    const [pages, setPages] = useState(defaultPages);
    const [splitByPhase, setSplitByPhase] = useState(false);
    const [savedPhaseFilter, setPhaseFilter] = useState<string[]>([]);

    // a saved phase that no opening has anymore is ignored
    const phaseFilter = savedPhaseFilter.filter(id => props.openings.some(o => phaseId(o.phase) === id));
    const ddlOptionsRef = useDropdownDataIMP()
    const [openings, setOpenings] = useState<OpeningWithSelected[]>([]);

    useEffect(() => {
        setOpenings(props.openings.map(o => ({
            ...o,
            selected: true
        })));
    }, [props.openings]);

    const saveAndClose = useSnackAction(async (input) => {
        await prefs.save(input);
        props.onDone();
    }, {
        success: "Saved preferences",
    })

    const {getContractorDetails, CurrentContractorPromptElement} = useCurrentContractor({
        onAutoPromptCanceled: props.onDone
    });

    const doGenerate = async () => {
        await prefs.save({
            version: 2,
            columns: selected.map(c => c.key),
            preamble: preamble,
            pages: pages,
            splitByPhase: splitByPhase,
            phaseFilter: savedPhaseFilter,
        })

        if(!project) return;
        const {client, clientContact} = await getContractorDetails();


        await printCtx.print2(getShopDrawingPDF({
            projectId: project.id,
            getData: async () => {
                const openingsInner = openings.filter(o => o.selected && matchesPhaseFilter(o.phase, phaseFilter));
                orderByAscending(openingsInner, o => o.seqNumber)

                const ddlOptions = await ddlOptionsRef.current();

                return {
                    pages,
                    preamble,
                    splitByPhase,
                    columns: selected,
                    project,
                    drawingInfo: makeDrawingInfo(openingsInner, ddlOptions),
                    lastSubmission: prefs.lastSubmission,
                    client,
                    clientContact,
                    data: openingsInner,
                    alternative: isQuote ? alternative : undefined,
                }
            }
        }));

        props.onDone();
    }

    // gate generation on elevation renderings being ready: if any are still pending/failed, prompt to
    // render-now-and-wait or proceed with placeholders (see SubmittalRenderGate). Wrapped so a failure
    // (e.g. the render/refresh call) surfaces in the action bar's error alert instead of being swallowed.
    // Declared before the early returns below so this hook runs on every render.
    const submit = useAsyncAction(async () => {
        if(!project) return;

        // re-render (in place) any renderings left by an older render version first, so the readiness
        // check below sees them as pending and the gate waits for the current-code images.
        await api.elevationTool.refreshRenderings({project: project.id});

        const status = await api.elevationTool.renderingStatus({project: project.id});
        if(status.pending > 0 || status.failed > 0) {
            setRenderGate(status);
            return;
        }

        await doGenerate();
    }, [project, doGenerate]);

    if(CurrentContractorPromptElement) {
        return (<>{CurrentContractorPromptElement}</>);
    }

    if(prefs.loading) return null;

    if(project?.quotePausePricing) {
        return (
            <Dialog2 open maxWidth="md" fullWidth onClose={props.onDone}>
                <DialogTitle>Create Shop Drawing Submittal</DialogTitle>
                <PausePricingCover />
                <DialogActions>
                    <Button onClick={props.onDone}>Cancel</Button>
                </DialogActions>
            </Dialog2>
        )
    }

    return (
        <>
        <Dialog2 open maxWidth="lg" fullWidth onClose={props.onDone} onEnterPress={() => submit.callback(undefined)}>
            <DialogTitle>Create Shop Drawing Submittal</DialogTitle>
            <TabWrapper>
                <Tabs className={tabGroup} value={tab} onChange={(e, value) => setTab(value)}>
                    <Tab label="Pages" value="pages" />
                    <Tab label={
                        <span style={{display: "inline-flex", alignItems: "center", gap: 6}}>
                            Preamble
                            {hasPreambleContent(preamble) && (
                                <span style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: "#1976d2",
                                    display: "inline-block",
                                }} />
                            )}
                        </span>
                    } value="preamble" />
                    <Tab label="Columns" value="columns" />
                    <Tab label="Openings" value="openings" />
                </Tabs>
            </TabWrapper>
            <DialogContent>

                {tab === "pages" ?
                    <>
                        <PagesTab value={pages} onChange={setPages} />
                        <List dense>
                            <ListItemButton onClick={() => setSplitByPhase(!splitByPhase)} dense>
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={splitByPhase}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                </ListItemIcon>
                                <ListItemText primary="Break down openings by phase" />
                            </ListItemButton>
                        </List>
                        <div style={{paddingLeft: 16, paddingRight: 16, paddingTop: 8}}>
                            <PhaseFilterSelect phases={props.openings.map(o => o.phase)} value={phaseFilter} onChange={setPhaseFilter} />
                        </div>
                    </> :
                        tab === "columns" ?
                            <ColumnTab value={selected} onChange={setSelected} sample={props.openings[0]} columns={openingColumns} /> :
                            tab === "openings" ?
                                <OpeningsTab value={openings} onChange={setOpenings} /> :
                                <PreambleTab value={preamble} onChange={setPreamble} />}
            </DialogContent>
            <SmoothActionBar
                loading={submit.loading}
                error={submit.error}
                onCancel={() => {
                    saveAndClose.callback({
                        version: 2,
                        columns: selected.map(c => c.key),
                        preamble: preamble,
                        pages: pages,
                        splitByPhase: splitByPhase,
                        phaseFilter: savedPhaseFilter,
                    })
                }}
                onSubmit={() => submit.callback(undefined)}
                actionName="Create and Download"
                submittingWord="Creating…"
            />
        </Dialog2>
        {renderGate && project && <SubmittalRenderGate
            projectId={project.id}
            status={renderGate}
            onProceed={async () => {
                setRenderGate(null);
                await doGenerate();
            }}
            onCancel={() => setRenderGate(null)}
        />}
        </>
    )
}

export function getShopDrawingPDF(props: {
    projectId: number;
    getData: () => Promise<{
        preamble: string;
        columns: ExportColumn[]
        project: Project;
        drawingInfo: DrawingInfo;
        lastSubmission: string|null;
        client?: Company;
        clientContact?: Contact;
        data: (Opening|QuoteOpening)[];
        pages: PageItem[];
        // quote mode: the alternative the openings belong to.
        alternative?: number;
        splitByPhase: boolean;
    }>
}): PDFInfo {

    return {
        name: `shop-drawing.pdf`,
        render: async (onReady, config) => {
            const d = await props.getData();

            const screenElevations = distinct(d.data.map(d => d.screenElevationFile).filter(v => !!v)) as number[]
            let doorElevations = distinct(d.data.flatMap(d => {
                if("doorElevationFile" in d || "inactiveDoorElevationFile" in d) return [d.doorElevationFile, d.inactiveDoorElevationFile];
                return [];
            }).filter(v => !!v)) as number[];
            let liteKitProfiles = distinct(d.data.flatMap(d => {
                if("doorLiteKitActiveFile" in d || "doorLiteKitInactiveFile" in d) return [d.doorLiteKitActiveFile, d.doorLiteKitInactiveFile];
                return [];
            }).filter(v => !!v)) as number[];
            let frameProfiles = distinctByKey(d.data.filter(d => !!d.frameProfileFile && !!d.frameProfile), v => v.frameProfile);

            // drw elevation-tool renderings (distinct file per rendering, sorted by label), gated
            // on their page. The label prints as a tile footer — it is no longer baked into the image.
            const screenRenderings = d.pages.includes("screenElevations")
                ? distinctRenderingsByLabel(d.data, o => "drwScreenRendering" in o
                    ? {file: o.drwScreenRendering, label: elevationDrawingValue(drwElevationName(o, "screen"), o.drwScreenElevationLabel)}
                    : undefined)
                : [];
            const doorRenderings = d.pages.includes("doorElevations")
                ? distinctRenderingsByLabel(d.data, o => "drwDoorRendering" in o
                    ? {file: o.drwDoorRendering, label: elevationDrawingValue(drwElevationName(o, "door"), o.drwDoorElevationLabel)}
                    : undefined)
                : [];
            const hardwareRenderings = d.pages.includes("hardwareElevations")
                ? distinctRenderingsByLabel(d.data, o => "drwHardwareRendering" in o
                    ? {file: o.drwHardwareRendering, label: elevationDrawingValue(drwElevationName(o, "hardware"), o.drwHardwareElevationLabel)}
                    : undefined)
                : [];

            if(!d.pages.includes("frameProfiles")) {
                frameProfiles = [];
            }

            if(!d.pages.includes("doorElevations")) {
                doorElevations = [];
            }

            if(!d.pages.includes("liteKitProfiles")) {
                liteKitProfiles = [];
            }

            const anchors = await api.products.listById({
                ids: distinct(d.data.flatMap(o => o.drwHasAnchors ? (o.drwAnchorProducts || []) : [o.frameAnchorProduct]).filter(v => !!v)) as number[]
            });

            const anchorProductsForFiles = distinct(anchors.filter(d => d.catalogs && d.catalogs.length > 0).map(d => d.id).filter(v => !!v)) as number[]

            const screenElevationImgP = Promise.all(screenElevations.map(s => api.print.fileAsImage({file: s})));
            const doorElevationImageP = Promise.all(doorElevations.map(async s => {
                const image = await api.print.fileAsImage({file: s})

                const activeNames = distinct(d.data.filter(d => d.doorElevationFile === s).map(d => d.doorElevation).filter(v => !!v));
                const inactiveNames = distinct(d.data.filter(d => d.inactiveDoorElevationFile === s).map(d => d.inactiveDoorElevation).filter(v => !!v));
                const names = [...activeNames, ...inactiveNames];

                const combined = names.map(n => {
                    const active = d.data.find(d => d.doorElevation === n && !!d.doorLiteKitActiveFile);
                    if(active) {
                        return {
                            name: n,
                            profileFile: active.doorLiteKitActiveFile || null,
                        };
                    }

                    const inactive = d.data.find(d => d.inactiveDoorElevation === n && !!d.doorLiteKitInactiveFile);
                    if(inactive) {
                        return {
                            name: n,
                            profileFile: inactive.doorLiteKitInactiveFile || null,
                        };
                    }

                    return {
                        name: n,
                        profileFile: null,
                    }
                });

                return {
                    image: image,
                    names: combined,
                }
            }));
            const liteKitProfileImageP = Promise.all(liteKitProfiles.map(async s => {
                const image =  await api.print.fileAsImage({file: s});
                return {
                    image: image,
                    profileFile: s,
                }
            }));
            const anchorImagesP = Promise.all(anchorProductsForFiles.map(s => api.print.fileAsImage({catalogForProduct: s})));
            const frameProfileImagesP = Promise.all(frameProfiles.map(async s => {
                if(!s.frameProfileFile) throw new Error("Frame profile file is missing");

                return await api.print.fileAsImage({file: s.frameProfileFile});
            }));
            const screenRenderingImagesP = Promise.all(screenRenderings.map(async s => ({image: await api.print.fileAsImage({file: s.file}), label: s.label})));
            const doorRenderingImagesP = Promise.all(doorRenderings.map(async s => ({image: await api.print.fileAsImage({file: s.file}), label: s.label})));
            const hardwareRenderingImagesP = Promise.all(hardwareRenderings.map(async s => ({image: await api.print.fileAsImage({file: s.file}), label: s.label})));
            const glazingSectionsP = d.pages.includes("liteKitProfiles")
                ? loadGlazingSections({project: d.project.id, alternative: d.alternative, openings: d.data.map(o => o.id)})
                : Promise.resolve({screens: [], doors: []});

            const screenElevationImages = await screenElevationImgP;
            const doorElevationImages = await doorElevationImageP;
            const liteKitProfileImages = await liteKitProfileImageP;
            const anchorImages = await anchorImagesP;
            const frameProfileImages = await frameProfileImagesP;
            const screenRenderingImages = await screenRenderingImagesP;
            const doorRenderingImages = await doorRenderingImagesP;
            const hardwareRenderingImages = await hardwareRenderingImagesP;
            const glazingSections = await glazingSectionsP;

            // the drw drawing columns are appended to the openings table when their page is on.
            const columns = [
                ...d.columns,
                ...(d.pages.includes("screenElevations") ? [screenDrawingColumn] : []),
                ...(d.pages.includes("doorElevations") ? [doorDrawingColumn] : []),
                ...(d.pages.includes("hardwareElevations") ? [hardwareDrawingColumn] : []),
            ];

            const doorElevationImages2: {image: string, name: string, profileFile: number | null, profile: string | null}[] = [];
            doorElevationImages.forEach(d => {
                d.names.forEach(n => {
                    doorElevationImages2.push({
                        image: d.image,
                        name: n.name,
                        profile: liteKitProfileImages.find(p => p.profileFile === n.profileFile)?.image || null,
                        profileFile: n.profileFile,
                    })
                });
            });

            const doorElevationsSimple = doorElevationImages.map(d => d.image);
            const liteKitProfileImages2 = liteKitProfileImages.map(l => l.image)

            return (
                <OpeningsPDF
                    pages={d.pages}
                    splitByPhase={d.splitByPhase}
                    preamble={d.preamble} columns={columns}
                    project={d.project}
                    drawingInfo={d.drawingInfo}
                    lastSubmittal={d.lastSubmission}
                    client={d.client}
                    clientContact={d.clientContact}
                    data={d.data} onReady={onReady}
                    screenElevations={screenElevationImages}
                    doorElevations={doorElevationImages2}
                    doorElevationsSimple={doorElevationsSimple}
                    screenRenderings={screenRenderingImages}
                    doorRenderings={doorRenderingImages}
                    hardwareRenderings={hardwareRenderingImages}
                    liteKitProfiles={liteKitProfileImages2}
                    glazingScreens={glazingSections.screens}
                    glazingDoors={glazingSections.doors}
                    anchorImages={anchorImages}
                    frameProfileImages={frameProfileImages}
                />
            );
        },
        serverInfo: {
            submissionForProject: props.projectId,
            submissionType: "shop-drawing",
        }
    }
}

export type DrawingInfo = {
    handings: string[]
    cores: string[];
    seams: string[];
    construction: string[]
    anchors: string[]

    doorMaterialsLookup: {[key: string]: string};
    doorMaterialsAbbreviations: {[key: string]: string};
    doorFinishAbbreviations: {[key: string]: string};
    doorFinishesLookup: {[key: string]: string};
    frameProfileAbbreviations: {[key: string]: string};
}

export const blankDrawingInfo: DrawingInfo = {
    handings: [],
    cores: [],
    seams: [],
    construction: [],
    anchors: [],
    doorMaterialsLookup: {},
    doorMaterialsAbbreviations: {},
    doorFinishAbbreviations: {},
    doorFinishesLookup: {},
    frameProfileAbbreviations: {}
}

function removeBlanks(value: string | null | undefined) {
    return !!value;
}

function withLookup(lookup: (value: string) => {value: string; display: string} | null) {
    return (value: string) => {
        return lookup(value)?.display;
    }
}

type ColumnList = string[]
export type ShopDrawingPrefV1 = {
    version: 1
    columns: string[];
    preamble: string;
}
export type ShopDrawingPrefV2 = {
    version: 2
    columns: string[];
    preamble: string;
    pages: string[];
    splitByPhase?: boolean;
    phaseFilter?: string[];
}
export type ShopDrawingSubmittalPrefs = ColumnList | ShopDrawingPrefV1 | ShopDrawingPrefV2;

export type OpeningSizeSubmittalPrefs = {
     version: 1;
     columns: string[]
};

export function makeDrawingInfo(ctx: (Opening|QuoteOpening)[], ddlOptions: ValueItem[]): DrawingInfo {
    const handings = distinct(ctx.map(o => o.handing).filter(v => !!v))
        .map(withLookup(handingLookup))
        .filter(removeBlanks) as string[];

    const doorMaterialsLookup: {[key:string]: string} = {};
    const doorMaterialsAbbreviations: {[key:string]: string} = {};

    [...ctx.filter(o => o.doorSeries === hollowMetalDoor)
        .map(o => o.doorMaterial),
    ...ctx.filter(o => o.frameSeries === hollowMetalFrame)
        .map(o => o.frameMaterial),
    ]
        .forEach(value => {
            const mat = withLookup(lookupHollowMetalMaterial)(value);
            if(!mat) return;

            const parts = mat.split(":").map(p => p.trim())
            doorMaterialsAbbreviations[parts[0]] = parts[1]
            doorMaterialsLookup[value] = parts[0]
        });

    const frameProfileAbbreviations: {[key: string]: string} = {};
    ctx.filter(o => o.frameProfile !== "").forEach(v => {
        const abbr = frameProfileAbbreviations[v.frameProfile];
        if(!abbr) {
            const val = ddlOptions.find(c => c.category === "frameProfile" && c.value === v.frameProfile);
            if(val) {
                frameProfileAbbreviations[val.value] = val.printLabel || val.label || val.value;
            }
        }
    })

    let customDoorMaterials: string[] = [];

    ctx
        .filter(o => o.doorSeries !== hollowMetalDoor)
        .forEach(o => {
            const mat = o.doorMaterial
            if(!mat) return;

            if(mat === woodPLAM) {
                doorMaterialsAbbreviations[woodPLAM] = "Plastic Laminate"
                doorMaterialsLookup[mat] = mat
                return;
            }

            let abbr: string | null = null;

            if(ddlOptions.some(c => c.category === "doorMaterial" && c.doorSeries === woodDoor && c.value === mat)) {
                abbr = mat;
            }

            if(!abbr) {
                let index = customDoorMaterials.indexOf(mat)
                if(index === -1) {
                    customDoorMaterials.push(mat)
                    index = customDoorMaterials.length - 1
                }

                abbr = `M${index}`
            }

            if(doorMaterialsAbbreviations.hasOwnProperty(abbr) && doorMaterialsAbbreviations[abbr] !== mat) {
                throw new Error("Duplicate abbreviation for door material: " + abbr)
            }

            doorMaterialsAbbreviations[abbr] = mat;
            doorMaterialsLookup[mat] = abbr;
        });

    let myDoorFinishAbbreviations: {[key: string]: string} = {}
    let doorFinishesLookup: {[key: string]: string} = {}
    let customDoorFinishes: string[] = [];

    ctx.forEach(v => {
        const finish = v.doorFinish
        if(!finish) return;

        let abbr: string | null = null;
        if(ddlOptions.some(s => s.category === "doorMaterialWoodFinish" && s.value === finish)) {
            abbr = finish;
        }

        if(!abbr) {
            let i = customDoorFinishes.indexOf(finish)
            if(i === -1) {
                customDoorFinishes.push(finish)
                i = customDoorFinishes.length - 1
            }

            abbr = `F${i}`
        }

        if(myDoorFinishAbbreviations[abbr] && myDoorFinishAbbreviations[abbr] !== finish) {
            throw new Error("Duplicate abbreviation for door finish: " + abbr)
        }

        myDoorFinishAbbreviations[abbr] = finish;
        doorFinishesLookup[finish] = abbr;
    })



    const cores = distinct(ctx.map(o => ddlOptions.find(c => c.category === "doorCore" && c.value === o.doorCore)?.display))
        .filter(removeBlanks) as string[]
    const seams = distinct(ctx.map(o => o.doorSeam))
        .map(o => ddlOptions.find(c => c.category === "doorSeam" && c.value === o)?.display)
        .filter(removeBlanks) as string[]
    const construction = distinct(ctx.map(o => o.frameConstruction)).map(withLookup(frameConstructionLookup)).filter(removeBlanks) as string[]
    const anchorAbbr = anchorLookupOptions();
    const anchors = (distinct(ctx.map(o => o.frameAnchorProductName)).filter(removeBlanks) as string[])
            .map(o => o.split(":")[0].trim())
            .map(abbr => anchorAbbr.find(a => a.value === abbr)?.display).filter(removeBlanks) as string[];

    return {
        anchors,
        handings,
        doorMaterialsLookup,
        doorMaterialsAbbreviations,
        doorFinishAbbreviations: myDoorFinishAbbreviations,
        doorFinishesLookup,
        frameProfileAbbreviations,
        cores,
        seams,
        construction,
    }
}